import React, { useMemo } from 'react';
import { observer } from 'mobx-react';

import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

import clsx from 'clsx';

import { dateUtilities, numberUtilities } from 'utils';
import { useStore } from 'hooks';
import { useTranslation } from 'react-i18next';

const GeneralTab = ({ sensor }) => {
  const { t } = useTranslation();
  const { topologyManagementStore } = useStore();
  const handleRounding = val => (val ? numberUtilities.roundToDecimal(val) : 'N/A');

  const INFO = useMemo(() => {
    const { signal_strength, sensor_vfr, sensor_temp_c } =
      topologyManagementStore.selectedSensorMetadata || {};
    const { VOLUME_SCALE_FACTOR, PGA_GAIN, CALIBRATED_TOF } = sensor?.metadata || {};
    const sensorContentRows = [
      [
        {
          title: t('SystemConfiguration.signalStrength'),
          value: handleRounding(signal_strength),
        },
        {
          title: t('SystemConfiguration.volScale'),
          value: handleRounding(VOLUME_SCALE_FACTOR),
        },
      ],
      [
        {
          title: t('SystemConfiguration.gain'),
          value: handleRounding(PGA_GAIN),
        },
        {
          title: t('SystemConfiguration.calTOF'),
          value: numberUtilities.toExponential(CALIBRATED_TOF),
        },
      ],
      [
        {
          title: t('SystemConfiguration.VFR'),
          value: handleRounding(sensor_vfr),
        },
        {
          title: t('SystemConfiguration.temperature'),
          value: handleRounding(sensor_temp_c),
        },
      ],
    ];

    return sensorContentRows.map((row, rowIndex) => (
      <Box
        className={clsx('item row', {
          hidden: !topologyManagementStore.selectedSensorMetadata,
        })}
        key={rowIndex}
      >
        {row.map(({ title, value }, elementIndex) => (
          <Box className="item_row" key={rowIndex * 10 + elementIndex}>
            <Typography
              gutterBottom
              color={({ palette }) => palette.whiteEmphasis.medium}
              variant="h8"
            >
              {title}
            </Typography>
            <Typography variant="h7">{value}</Typography>
          </Box>
        ))}
      </Box>
    ));
  }, [t, sensor.metadata, topologyManagementStore.selectedSensorMetadata]);

  return (
    <StyledRootBox>
      <Box className="item">
        <Typography gutterBottom color={({ palette }) => palette.whiteEmphasis.medium} variant="h8">
          {t('SystemConfiguration.latestPour')}
        </Typography>
        <Typography variant="h7">
          {dateUtilities.formatDateIncludingToday(sensor.latest_pour_received_at)}
        </Typography>
      </Box>
      {INFO}
    </StyledRootBox>
  );
};

export default observer(GeneralTab);

const StyledRootBox = styled(Box)(({ theme: { palette } }) => ({
  display: 'flex',
  flexDirection: 'column',

  '&.hidden': {
    visibility: 'hidden',
  },

  '.item': {
    display: 'flex',
    flexDirection: 'column',
    padding: '8px 0',
    borderBottom: `1px solid ${palette.secondary.main}`,

    '&.row': {
      flexDirection: 'row',
    },
  },

  '.item_row': {
    display: 'flex',
    flexDirection: 'column',
    flexBasis: '50%',

    '&.hidden': {
      visibility: 'hidden',
    },
  },
}));
